/**
 * dialog - the implementation. Public entry: index.js.
 *
 * Programmatic modals: the lib builds the Bootstrap modal, shows it, returns
 * a Promise and removes the DOM node on close. No authored markup, no
 * instances to manage. The confirm/OK button carries key-hint="Enter", so the
 * g.shortcuts ? panel lists it and Enter activates it while the focus sits on
 * the dialog (focus in the prompt input submits natively instead - no
 * double-fire, see genus shortcuts).
 *
 * Full reference: docs/libs/dialog.md.
 */

const g = window.g;

let seq = 0;

// Escape a plain-text message for the modal body ('\n' becomes <br>).
const textBody = (message, html) => (html ? String(message ?? '') : g.esc(message).replace(/\n/g, '<br>'));

function buttonHtml(btn, index) {
	const hint = btn.keyHint ? ` key-hint="${g.esc(btn.keyHint)}"` : '';
	// start: true pushes the button to the footer's left edge (the Cancel side
	// of the house button standard - see docs/libs/dialog.md).
	return `<button type="button" class="btn btn-${btn.variant || 'primary'}${btn.start ? ' me-auto' : ''}" data-dialog-index="${index}"${hint}>${g.esc(btn.text || 'OK')}</button>`;
}

/**
 * The generic dialog. Resolves with the clicked button's `value`, or with
 * `opts.dismissValue` when closed any other way (Escape, backdrop, x).
 *
 * opts: title, body (HTML - escape your data with g.esc), size ('sm'|'lg'|
 * 'xl'), buttons [{ text, variant, value, keyHint, onClick(el) -> false
 * keeps the dialog open }], closeButton (default true), backdrop, keyboard,
 * focus (selector; default: [data-dialog-focus] or the last button),
 * dismissValue, onShow(el, settle).
 */
export function dialog(opts = {}) {
	return new Promise((resolve) => {
		const buttons = opts.buttons || [{ text: g.translate('OK'), variant: 'primary', value: true, keyHint: 'Enter' }];
		const header = opts.title ? `<div class="modal-header"><h3 class="modal-title">${g.esc(opts.title)}</h3>${opts.closeButton === false ? '' : `<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${g.translate('Close')}"></button>`}</div>` : '';
		const wrap = document.createElement('div');
		wrap.innerHTML = `<div class="modal fade genus-dialog" tabindex="-1" aria-hidden="true"><div class="modal-dialog modal-dialog-centered${opts.size ? ` modal-${opts.size}` : ''}"><div class="modal-content">${header}<div class="modal-body">${opts.body || ''}</div><div class="modal-footer">${buttons.map(buttonHtml).join('')}</div></div></div></div>`;
		const el = wrap.firstElementChild;
		document.body.appendChild(el);

		const modal = new bootstrap.Modal(el, { backdrop: opts.backdrop === undefined ? true : opts.backdrop, keyboard: opts.keyboard !== false });
		let settled = false;
		let value = opts.dismissValue;
		const settle = (v) => {
			if (!settled) { settled = true; value = v; }
			modal.hide();
		};

		el.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-dialog-index]');
			if (!btn) return;
			const def = buttons[+btn.dataset.dialogIndex] || {};
			if (def.onClick && def.onClick(el) === false) return; // stays open (validation)
			settle(def.value);
		});
		el.addEventListener('shown.bs.modal', () => {
			const target = (opts.focus && el.querySelector(opts.focus)) || el.querySelector('[data-dialog-focus]') || Array.from(el.querySelectorAll('[data-dialog-index]')).pop();
			if (target) { target.focus(); if (target.select) target.select(); }
		});
		el.addEventListener('hidden.bs.modal', () => {
			modal.dispose();
			el.remove();
			resolve(value);
		});

		if (opts.onShow) opts.onShow(el, settle);
		modal.show();
	});
}

/**
 * g.confirm(message, opts) -> Promise<boolean> (false on dismiss).
 * opts: title, danger (red confirm button), confirmText, cancelText, html.
 */
export function confirm(message, opts = {}) {
	return dialog({
		title: opts.title || g.translate('Are you sure?'),
		body: `<p class="mb-0">${textBody(message, opts.html)}</p>`,
		size: opts.size,
		dismissValue: false,
		buttons: [
			{ text: opts.cancelText || g.translate('Cancel'), variant: 'soft-outline-secondary', start: true, value: false },
			{ text: opts.confirmText || g.translate('Confirm'), variant: opts.danger ? 'danger' : 'primary', value: true, keyHint: 'Enter' },
		],
	}).then((v) => !!v);
}

/**
 * g.alert(message, opts) -> Promise (resolves when acknowledged/closed).
 * opts: title, okText, html.
 */
export function alert(message, opts = {}) {
	return dialog({
		title: opts.title || '',
		body: `<p class="mb-0">${textBody(message, opts.html)}</p>`,
		size: opts.size,
		dismissValue: undefined,
		buttons: [{ text: opts.okText || g.translate('OK'), variant: 'primary', value: undefined, keyHint: 'Enter' }],
	});
}

/**
 * g.codePrompt(message, opts) -> Promise<true|string|null>.
 *
 * THE house verification-code dialog — every email/SMS-code confirmation
 * that runs inside the app uses this (the register/reset PAGES carry the
 * same look as inline panels). One wide `.verify-code-input` box (centered,
 * digits spaced like the code in the message), Enter submits, and an
 * optional resend line with the register-style cooldown countdown.
 *
 * opts:
 * - confirm(code) -> Promise<string|falsy>: the SERVER-side check. A string
 *   is the error to show under the input (the dialog stays open and
 *   shakes); falsy closes the dialog and resolves `true`. Without it the
 *   dialog resolves the typed code (client-side flows).
 * - resend() -> Promise<true|string|false>: re-request the code. `true`
 *   toasts the standard success and restarts the countdown; a string is
 *   toasted as the error; `false` toasts a generic failure. Omitting
 *   `resend` hides the line.
 * - cooldown (seconds, default 60; the countdown starts on open — the flow
 *   just SENT a code), length (default 6), title, danger (red confirm
 *   button), okText, cancelText, html.
 * Cancel/dismiss resolve null. The DB enforces the real cooldown/attempt
 * limits — the countdown is a courtesy mirror.
 */
export function codePrompt(message, opts = {}) {
	const id = `genus-code-${++seq}`;
	const len = opts.length || 6;
	const resendRow = opts.resend
		? `<p class="text-body-secondary text-center mt-3 mb-0">${g.esc(g.translate('Nothing arrived?'))} <a href="#" data-dialog-resend>${g.esc(g.translate('Send a new code'))}</a> <span class="text-body-secondary" data-dialog-countdown></span></p>`
		: '';
	const body = `<form data-dialog-form novalidate><label class="form-label" for="${id}">${textBody(message, opts.html)}</label><input type="text" class="form-control verify-code-input" id="${id}" data-dialog-focus inputmode="numeric" autocomplete="one-time-code" maxlength="${len}" placeholder="${'·'.repeat(len)}" aria-label="${g.esc(g.translate('Verification code'))}" /></form>${resendRow}`;
	let busy = false;
	return dialog({
		title: opts.title || g.translate('Confirmation code'),
		body,
		size: opts.size,
		dismissValue: null,
		buttons: [
			{ text: opts.cancelText || g.translate('Cancel'), variant: 'soft-outline-secondary', start: true, value: null },
			{ text: opts.okText || g.translate('Confirm'), variant: opts.danger ? 'danger' : 'primary', keyHint: 'Enter', onClick: (el) => { el.querySelector('[data-dialog-form]').requestSubmit(); return false; } },
		],
		onShow: (el, settle) => {
			const form = el.querySelector('[data-dialog-form]');
			const input = form.querySelector('input');
			const anchor = el.querySelector('[data-dialog-resend]');
			const label = el.querySelector('[data-dialog-countdown]');
			let timer = null;
			const countdown = (seconds) => {
				anchor.hidden = true;
				let left = seconds;
				label.textContent = g.translate('(wait {n}s)', { n: left });
				timer = setInterval(() => {
					left--;
					if (left <= 0) {
						clearInterval(timer);
						label.textContent = '';
						anchor.hidden = false;
						return;
					}
					label.textContent = g.translate('(wait {n}s)', { n: left });
				}, 1000);
			};
			if (anchor) {
				if (opts.cooldown !== 0) countdown(opts.cooldown || 60);
				let resending = false; // a double-click fired resend() twice — two codes, two emails (race scan)
				anchor.addEventListener('click', async (e) => {
					e.preventDefault();
					if (resending) return;
					resending = true;
					const sent = await Promise.resolve(opts.resend()).finally(() => { resending = false; });
					if (sent === true) {
						g.success(g.translate('A new code is on its way.'));
						countdown(opts.cooldown || 60);
					} else {
						g.error(typeof sent === 'string' ? sent : g.translate('Could not send a new code.'));
					}
					// the link hid under the focus — back to the input, so typing
					// (and Escape, which Bootstrap hears on the modal) still work
					input.focus();
				});
			}
			el.addEventListener('hidden.bs.modal', () => { if (timer) clearInterval(timer); });
			// pasted codes arrive wrapped in the email's whitespace (a
			// triple-click grabs the surrounding text nodes) — sanitize
			// BEFORE insertion: maxlength would otherwise truncate a
			// leading space's worth of digits and the submit-time trim
			// comes too late
			input.addEventListener('paste', (e) => {
				e.preventDefault();
				input.value = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, len);
			});
			form.addEventListener('submit', async (e) => {
				e.preventDefault();
				if (busy) return;
				const code = input.value.trim();
				if (!new RegExp(`^[0-9]{${len}}$`).test(code)) {
					g.fieldError(input, g.translate('Enter the {n}-digit code from the message.', { n: len }));
					g.shake(el.querySelector('.modal-content'));
					return;
				}
				g.fieldError(input, null);
				if (!opts.confirm) { settle(code); return; }
				busy = true;
				const error = await opts.confirm(code);
				busy = false;
				if (error) {
					g.fieldError(input, error);
					g.shake(el.querySelector('.modal-content'));
					input.select();
					return;
				}
				settle(true);
			});
		},
	});
}

/**
 * g.prompt(message, opts) -> Promise<string|null> (null on cancel/dismiss).
 * opts: title, value, placeholder, type, required, validate(v) -> error
 * message or falsy, okText, cancelText. Enter submits (single-input form);
 * errors show under the input via g.fieldError.
 */
export function prompt(message, opts = {}) {
	const id = `genus-prompt-${++seq}`;
	const body = `<form data-dialog-form novalidate><label class="form-label" for="${id}">${textBody(message, opts.html)}</label><input type="${g.esc(opts.type || 'text')}" class="form-control" id="${id}" data-dialog-focus value="${g.esc(opts.value ?? '')}" placeholder="${g.esc(opts.placeholder ?? '')}" /></form>`;
	return dialog({
		title: opts.title || '',
		body,
		size: opts.size,
		dismissValue: null,
		buttons: [
			{ text: opts.cancelText || g.translate('Cancel'), variant: 'soft-outline-secondary', start: true, value: null },
			// requestSubmit routes the click through the same submit+validation
			// path as pressing Enter in the input; settle happens there.
			{ text: opts.okText || g.translate('OK'), variant: 'primary', keyHint: 'Enter', onClick: (el) => { el.querySelector('[data-dialog-form]').requestSubmit(); return false; } },
		],
		onShow: (el, settle) => {
			const form = el.querySelector('[data-dialog-form]');
			const input = form.querySelector('input');
			form.addEventListener('submit', (e) => {
				e.preventDefault();
				let error = null;
				if (opts.required && !input.value.trim()) error = g.translate('This field is required.');
				else if (opts.validate) error = opts.validate(input.value) || null;
				g.fieldError(input, error);
				if (!error) settle(input.value);
			});
		},
	});
}
